import { useRef, useState, useEffect } from "react";
import { useGetAnalyticsSummaryQuery } from "./featureSlice/analtyicsSliceApi";
import { baseQuery } from "./services/baseQuery";
import { registrationDB, scanDB } from "../src/utils/offlineDB";

const isBrowser = typeof window !== "undefined";

const theme = {
  gray: "#595A5B1A",
  primary: "#221bf0",
  lightPrimary: "#dfdefd",
  lightborder: "#f4f4fe",
};

function speak(text) {
  if (!isBrowser || !window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.lang = "en-IN";
  u.rate = 0.6;
  window.speechSynthesis.speak(u);
}

/* ================= UID DEBOUNCE ================= */
function createUIDDebouncer(delay = 3000) {
  let lastUID = null;
  let timer = null;

  return (uid, callback) => {
    if (uid === lastUID) return;
    lastUID = uid;
    callback(uid);
    clearTimeout(timer);
    timer = setTimeout(() => (lastUID = null), delay);
  };
}

/* ================= MAIN COMPONENT ================= */
export default function NFCScanner() {
  const [isScanning, setIsScanning] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState(null);
  const [time, setTime] = useState(new Date());

  const nfcRef = useRef(null);
  const debounceRef = useRef(createUIDDebouncer());

  /* ================= ANALYTICS API  ================= */
  const { data: analyticsData } = useGetAnalyticsSummaryQuery();
  

  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // ✅ ONLY source for shift & break
  const currentMeal = analyticsData?.data?.[0]?.cards_data?.current || {};

  /* ================= LIVE CLOCK ================= */
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  /* ================= START NFC ================= */
  async function startScan() {
    if (!isBrowser || !("NDEFReader" in window)) {
      alert("NFC available only on Chrome Android");
      return;
    }

    try {
      const reader = new window.NDEFReader();
      nfcRef.current = reader;
      await reader.scan();
      setIsScanning(true);

      reader.onreading = (event) => {
        const uid = event.serialNumber?.toLowerCase();
        if (!uid) return;
        debounceRef.current(uid, handleUID);
      };
    } catch {
      alert("NFC permission denied");
    }
  }






async function handleUID(uid) {
  const payload = {
    entity_id: uid,
    time: new Date().toISOString(),
  };

  /* =================================================
     🔹 OFFLINE MODE (NO INTERNET)
  ================================================= */
  if (!navigator.onLine) {
    try {
      const localUser = await registrationDB.getItem(uid);

      const allowed = !!localUser;

      setResult({
        uid,
        employeeName: localUser?.rfid_owner_name || "-",
        allowed,
        statusMessage: allowed
          ? "Offline verified"
          : "Card not registered",
      });

      setShowResult(true);

      speak(allowed ? "Meal allowed" : "Card not registered");

      // save scan to queue for later sync
      await scanDB.setItem(Date.now().toString(), payload);

      setTimeout(() => setShowResult(false), 3000);
    } catch (e) {
      console.error(e);
    }

    return; // ❗ stop here
  }

  /* =================================================
     🔹 ONLINE MODE (YOUR OLD LOGIC – SAME)
  ================================================= */
  try {
    const res = await fetch(`${BASE_URL}verification/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        entity_id: uid,
      }),
    });

    const json = await res.json();
    const record = json?.data?.[0];

    const allowed =
      json.status === "success" &&
      record?.verified === true &&
      record?.is_punched === false;

    setResult({
      uid: record?.entity_id ? record?.entity_id : uid,
      employeeName: record?.entity_name ? record?.entity_name : "-",
      allowed,
      statusMessage: record?.status_message,
    });

    setShowResult(true);

    speak(
      allowed
        ? "Meal allowed"
        : record?.is_punched
        ? "Meal already taken"
        : "Card not verified"
    );

    setTimeout(() => setShowResult(false), 3000);
  } catch (err) {
    console.error(err);

 
    await scanDB.setItem(Date.now().toString(), payload);

    speak("Saved offline. Will sync later.");
  }
}

  /* ================= SYNC OFFLINE SCANS ================= */
async function syncOfflineScans() {
  if (!navigator.onLine) return;

  try {
    const items = [];

    await scanDB.iterate((value, key) => {
      items.push({ key, value });
    });

    if (!items.length) return;

    console.log("Syncing offline scans...", items.length);

    for (const item of items) {
      await fetch(`${BASE_URL}consumers/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(item.value),
      });

      await scanDB.removeItem(item.key);
    }

    console.log("Sync complete ");
  } catch (err) {
    console.error("Sync failed", err);
  }
}

/* ================= AUTO SYNC WHEN ONLINE ================= */
useEffect(() => {
  window.addEventListener("online", syncOfflineScans);

  // try once on load also
  syncOfflineScans();

  return () =>
    window.removeEventListener("online", syncOfflineScans);
}, [syncOfflineScans]);



  return (
    <div className="min-h-screen p-6" style={{ background: theme.lightborder }}>
      <div className="flex justify-between items-center mb-6">
        <div className="font-semibold" style={{ color: theme.primary }}>
          Canteen Meal Punch-In
        </div>
        <div className="text-sm font-medium" style={{ color: theme.primary }}>
          ● SYSTEM ONLINE
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center min-h-[70vh]">
        <div>
          <h1
            className="font-bold leading-none"
            style={{ fontSize: "96px", color: theme.primary }}
          >
            {time.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </h1>

          <p className="mt-2 text-gray-600">
            {time.toLocaleDateString("en-IN", {
              weekday: "long",
              day: "numeric",
              month: "long",
            })}
          </p>

          <div className="mt-8 flex gap-6">
            <div
              className="p-4 rounded-xl w-48"
              style={{ background: theme.gray }}
            >
              <p className="text-sm text-gray-500">Current Shift</p>
              <p
                className="text-xl font-semibold"
                style={{ color: theme.primary }}
              >
                {currentMeal?.shift || "-"}
              </p>
            </div>

            <div
              className="p-4 rounded-xl w-48"
              style={{ background: theme.gray }}
            >
              <p className="text-sm text-gray-500">Service</p>
              <p
                className="text-xl font-semibold"
                style={{ color: theme.primary }}
              >
                {currentMeal?.meal_type
                  ? `${currentMeal.meal_type
                      .charAt(0)
                      .toUpperCase()}${currentMeal.meal_type.slice(1)} Break`
                  : "-"}
              </p>
              <p className="text-xs text-gray-500">
                {currentMeal?.start_time && currentMeal?.end_time
                  ? `${currentMeal.start_time} – ${currentMeal.end_time}`
                  : "-"}
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center">
          <div
            className="w-64 h-64 rounded-2xl border-2 border-dashed flex items-center justify-center"
            style={{ borderColor: theme.primary }}
          >
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center animate-pulse text-2xl"
              style={{ background: theme.lightPrimary }}
            >
              📶
            </div>
          </div>

          <p className="mt-4 font-semibold" style={{ color: theme.primary }}>
            Scan Employee Card
          </p>

          {!isScanning && (
            <button
              onClick={startScan}
              className="mt-4 px-6 py-2 rounded-lg font-semibold text-white"
              style={{ background: theme.primary }}
            >
              Enable Scanner
            </button>
          )}
        </div>
      </div>

      {showResult && result && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div
            className="rounded-2xl p-6 w-[420px] shadow-xl"
            style={{ background: theme.lightPrimary }}
          >
            <div
              className="text-center py-2 rounded-lg font-bold mb-4 text-white"
              style={{
                background: result.allowed ? theme.primary : "#dc2626",
              }}
            >
              {result.allowed ? "MEAL ALLOWED" : "MEAL NOT ALLOWED"}
            </div>

            <div className="flex items-center gap-4">
              <div
                className="w-16 h-16 rounded-full"
                style={{ background: theme.gray }}
              />
              <div>
                <p className="font-semibold">{result?.employeeName}</p>
                <p style={{ color: theme.primary }}>
                  Code: {result?.employeeCode}
                </p>
                <p className="text-xs text-gray-600">
                  Card UID: {result?.entity_id}
                </p>
                <p className="text-xs text-red-600">
                  Message: {result?.message}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// extra
// import { useRef, useState, useEffect } from "react";
// import { useGetAnalyticsSummaryQuery } from "./featureSlice/analtyicsSliceApi";
// import { useGetRfidRegistrationsQuery } from "./featureSlice/rfidRegistrationSliceApi";
// import { registrationDB, scanDB } from "../src/utils/offlineDB";

// const isBrowser = typeof window !== "undefined";

/* ================= THEME ================= */
// const theme = {
//   gray: "#595A5B1A",
//   primary: "#221bf0",
//   lightPrimary: "#dfdefd",
//   lightborder: "#f4f4fe",
// };

/* ================= SPEAK ================= */
// function speak(text) {
//   if (!isBrowser || !window.speechSynthesis) return;

//   setTimeout(() => {
//     window.speechSynthesis.cancel();
//     const u = new SpeechSynthesisUtterance(text);
//     u.lang = "en-IN";
//     u.rate = 0.6;
//     window.speechSynthesis.speak(u);
//   }, 100);
// }

/* ================= UID DEBOUNCE ================= */
// function createUIDDebouncer(delay = 3000) {
//   let lastUID = null;
//   let timer = null;

//   return (uid, callback) => {
//     if (uid === lastUID) return;
//     lastUID = uid;
//     callback(uid);
//     clearTimeout(timer);
//     timer = setTimeout(() => (lastUID = null), delay);
//   };
// }

/* =========================================================
   ================= MAIN COMPONENT =========================
========================================================= */
// export default function NFCScanner() {
//   const [isScanning, setIsScanning] = useState(false);
//   const [showResult, setShowResult] = useState(false);
//   const [result, setResult] = useState(null);
//   const [time, setTime] = useState(new Date());
//   const [online, setOnline] = useState(navigator.onLine);

//   const nfcRef = useRef(null);
//   const debounceRef = useRef(createUIDDebouncer());

//   const BASE_URL = process.env.REACT_APP_BASE_URL;

//   /* =========================================================
//      🔹 PRELOAD REGISTRATIONS (VERY IMPORTANT FOR OFFLINE)
//   ========================================================= */
//   const { data: regData } = useGetRfidRegistrationsQuery({
//     page: 1,
//     limit: 10000,
//   });

//   useEffect(() => {
//     if (!regData?.data) return;

//     regData.data.forEach((item) =>
//       registrationDB.setItem(item.rfid_uid, item)
//     );

//     console.log("✅ Registrations cached offline");
//   }, [regData]);

//   /* =========================================================
//      🔹 ANALYTICS
//   ========================================================= */
//   const { data: analyticsData } = useGetAnalyticsSummaryQuery();
//   const currentMeal = analyticsData?.data?.[0]?.cards_data?.current || {};

//   /* =========================================================
//      🔹 CLOCK
//   ========================================================= */
//   useEffect(() => {
//     const t = setInterval(() => setTime(new Date()), 1000);
//     return () => clearInterval(t);
//   }, []);

//   /* =========================================================
//      🔹 ONLINE / OFFLINE STATUS
//   ========================================================= */
//   useEffect(() => {
//     const goOnline = () => setOnline(true);
//     const goOffline = () => setOnline(false);

//     window.addEventListener("online", goOnline);
//     window.addEventListener("offline", goOffline);

//     return () => {
//       window.removeEventListener("online", goOnline);
//       window.removeEventListener("offline", goOffline);
//     };
//   }, []);

//   /* =========================================================
//      🔹 START NFC
//   ========================================================= */
//   async function startScan() {
//     if (!("NDEFReader" in window)) {
//       alert("NFC available only on Chrome Android");
//       return;
//     }

//     const reader = new window.NDEFReader();
//     nfcRef.current = reader;

//     await reader.scan();
//     setIsScanning(true);

//     reader.onreading = (event) => {
//       const uid = event.serialNumber?.toLowerCase();
//       if (!uid) return;
//       debounceRef.current(uid, handleUID);
//     };
//   }

//   /* =========================================================
//      🔹 HANDLE UID
//   ========================================================= */
//   // async function handleUID(uid) {
//   //   const payload = {
//   //     entity_id: uid,
//   //     time: new Date().toISOString(),
//   //   };

//   //   /* ================= OFFLINE ================= */
//   //   if (!navigator.onLine) {
//   //     const localUser = await registrationDB.getItem(uid);

//   //     const allowed = !!localUser;

//   //     setResult({
//   //       uid,
//   //       entity_id: uid,
//   //       employeeName: localUser?.rfid_owner_name || "-",
//   //       employeeCode: localUser?.employee_code || "-",
//   //       allowed,
//   //       message: allowed ? "Offline verified" : "Card not registered",
//   //     });

//   //     setShowResult(true);

//   //     speak(allowed ? "Meal allowed" : "Card not registered");

//   //     await scanDB.setItem(Date.now().toString(), payload);

//   //     setTimeout(() => setShowResult(false), 3000);
//   //     return;
//   //   }

//   //   /* ================= ONLINE ================= */
//   //   try {
//   //     const res = await fetch(`${BASE_URL}verification/verify`, {
//   //       method: "POST",
//   //       headers: { "Content-Type": "application/json" },
//   //       body: JSON.stringify({ entity_id: uid }),
//   //     });

//   //     const json = await res.json();
//   //     const record = json?.data?.[0];

//   //     const allowed =
//   //       json.status === "success" &&
//   //       record?.verified &&
//   //       !record?.is_punched;

//   //     setResult({
//   //       uid,
//   //       entity_id: record?.entity_id,
//   //       employeeName: record?.entity_name,
//   //       employeeCode: record?.employee_code,
//   //       allowed,
//   //       message: record?.status_message,
//   //     });

//   //     setShowResult(true);

//   //     speak(
//   //       allowed
//   //         ? "Meal allowed"
//   //         : record?.is_punched
//   //         ? "Meal already taken"
//   //         : "Card not verified"
//   //     );

//   //     setTimeout(() => setShowResult(false), 3000);
//   //   } catch {
//   //     await scanDB.setItem(Date.now().toString(), payload);
//   //     speak("Saved offline. Will sync later.");
//   //   }
//   // }
//   async function handleUID(uid) {
//   const payload = {
//     entity_id: uid,
//     time: new Date().toISOString(),
//   };

//   /* =================================================
//      🔴 OFFLINE MODE (ONLY NAME SHOW — NO CHECK)
//   ================================================= */
//   if (!navigator.onLine) {
//     const localUser = await registrationDB.getItem(uid);

//     setResult({
//       uid,
//       entity_id: uid,
//       employeeName: localUser?.rfid_owner_name || "Unknown Card",
//       employeeCode: localUser?.employee_code || "-",
//       message: "Offline saved",
//       offline: true, // ⭐ flag
//     });

//     setShowResult(true);

//     speak(localUser ? "Welcome" : "Unknown card");

//     await scanDB.setItem(Date.now().toString(), payload);

//     setTimeout(() => setShowResult(false), 3000);

//     return;
//   }

//   /* =================================================
//      🟢 ONLINE MODE (FULL VERIFY)
//   ================================================= */
//   try {
//     const res = await fetch(`${BASE_URL}verification/verify`, {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify({ entity_id: uid }),
//     });

//     const json = await res.json();
//     const record = json?.data?.[0];

//     const allowed =
//       json.status === "success" &&
//       record?.verified &&
//       !record?.is_punched;

//     setResult({
//       uid,
//       entity_id: record?.entity_id,
//       employeeName: record?.entity_name,
//       employeeCode: record?.employee_code,
//       message: record?.status_message,
//       allowed,
//       offline: false,
//     });

//     setShowResult(true);

//     speak(
//       allowed
//         ? "Meal allowed"
//         : record?.is_punched
//         ? "Meal already taken"
//         : "Card not verified"
//     );

//     setTimeout(() => setShowResult(false), 3000);
//   } catch {
//     await scanDB.setItem(Date.now().toString(), payload);
//     speak("Saved offline");
//   }
// }


//   /* =========================================================
//      🔹 AUTO SYNC WHEN ONLINE
//   ========================================================= */
//   useEffect(() => {
//     async function syncOfflineScans() {
//       if (!navigator.onLine) return;

//       const items = [];
//       await scanDB.iterate((value, key) =>
//         items.push({ key, value })
//       );

//       for (const item of items) {
//         await fetch(`${BASE_URL}consumers/`, {
//           method: "POST",
//           headers: { "Content-Type": "application/json" },
//           body: JSON.stringify(item.value),
//         });

//         await scanDB.removeItem(item.key);
//       }

//       if (items.length) console.log("✅ Offline scans synced");
//     }

//     window.addEventListener("online", syncOfflineScans);
//     syncOfflineScans();

//     return () =>
//       window.removeEventListener("online", syncOfflineScans);
//   }, []);

//   /* =========================================================
//      🔹 UI
//   ========================================================= */
//   return (
//     <div className="min-h-screen p-6" style={{ background: theme.lightborder }}>
//       <div className="flex justify-between mb-6">
//         <div style={{ color: theme.primary }}>Canteen Meal Punch-In</div>
//         <div style={{ color: theme.primary }}>
//           ● {online ? "ONLINE" : "OFFLINE"}
//         </div>
//       </div>

//       <h1 style={{ fontSize: 80, color: theme.primary }}>
//         {time.toLocaleTimeString([], {
//           hour: "2-digit",
//           minute: "2-digit",
//         })}
//       </h1>

//       <button onClick={startScan}>Enable Scanner</button>

//       {showResult && result && (
//         <div>
//           <h2>{result.offline
//   ? "OFFLINE SAVED"
//   : result.allowed
//   ? "MEAL ALLOWED"
//   : "MEAL NOT ALLOWED"}
// </h2>
//           <p>{result.employeeName}</p>
//           <p>{result.employeeCode}</p>
//           <p>{result.message}</p>
//         </div>
//       )}
//     </div>
//   );
// }
